// Reinforce an attack or raid (P31, G-70): own systems that can still arrive in the same second,
// ship rows limited to classes fast enough, a live departure time and the shield question.
import type { ReinforceOptionsResponse, ShipType } from '@stars-and-ships/api-types';
import { useState } from 'react';
import { selectionDeparture } from '../../fleetTiming';
import { formatDuration, formatShips, SHIP_ORDER } from '../../labels';
import { ShipRows } from '../dispatch/ShipRows';
import { parseAmount } from '../system/shared';
import { Button, ErrorNotice, Notice, Panel } from '../ui';

type Ships = { shipType: ShipType; count: number }[];

export function ReinforcePicker({
  options,
  name,
  now,
  pending,
  error,
  onSend,
}: {
  options: ReinforceOptionsResponse;
  name: (id: string | null | undefined) => string;
  now: number;
  pending: boolean;
  error: unknown;
  onSend: (systemId: string, ships: Ships, confirmShieldLift: boolean) => void;
}) {
  const [systemId, setSystemId] = useState<string | null>(null);
  const [counts, setCounts] = useState<Partial<Record<ShipType, string>>>({});
  const [confirmShield, setConfirmShield] = useState(false);
  const option = options.items.find((i) => i.systemId === systemId);
  // Only classes fast enough to make the arrival can be chosen.
  const feasible: Partial<Record<ShipType, number>> = option
    ? Object.fromEntries(
        Object.entries(option.ships).filter(([type]) => option.latestDeparture[type as ShipType])
      )
    : {};
  const ships: Ships = SHIP_ORDER.flatMap((shipType) => {
    const count = parseAmount(counts[shipType] ?? '');
    return count > 0 ? [{ shipType, count }] : [];
  });
  const departure = option ? selectionDeparture(option, ships) : null;
  const tooMany = ships.some((s) => s.count > (feasible[s.shipType] ?? 0));
  const shieldAsk = options.shieldWouldLift && !confirmShield;

  return (
    <>
      <Panel title="From">
        {options.items.length === 0 ? (
          <p className="text-sm text-muted">No system can arrive in time.</p>
        ) : (
          <ul className="space-y-2">
            {options.items.map((i) => {
              const leavesBy = Object.values(i.latestDeparture).reduce((a, b) => (a > b ? a : b));
              return (
                <li key={i.systemId}>
                  <Button
                    variant={i.systemId === systemId ? 'primary' : 'secondary'}
                    className="w-full justify-between"
                    onClick={() => {
                      setSystemId(i.systemId);
                      setCounts({});
                    }}
                  >
                    <span>{name(i.systemId)}</span>
                    <span className="text-xs">
                      {formatShips(i.ships)} · leaves by{' '}
                      {new Date(leavesBy).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
      {option ? (
        <Panel title="Ships">
          <ShipRows
            available={feasible}
            counts={counts}
            onChange={(type, value) => setCounts((prev) => ({ ...prev, [type]: value }))}
          />
          {departure ? (
            <p data-departure className="mt-2 text-sm text-muted">
              Departs at{' '}
              {new Date(departure).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}{' '}
              (in {formatDuration(Date.parse(departure) - now)})
            </p>
          ) : null}
          {error ? <ErrorNotice error={error} /> : null}
          {shieldAsk ? (
            <Notice tone="warning">
              <p>
                Attacking another commander lifts your beginner shield for good when the fleet
                leaves.
              </p>
              <Button className="mt-2" variant="danger" onClick={() => setConfirmShield(true)}>
                Lift shield when it leaves
              </Button>
            </Notice>
          ) : null}
          <Button
            className="mt-3 w-full"
            disabled={!departure || tooMany || shieldAsk}
            pending={pending}
            onClick={() => onSend(option.systemId, ships, confirmShield)}
          >
            Send
          </Button>
        </Panel>
      ) : null}
    </>
  );
}
